import React from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { Provider } from "react-redux";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import theme from "./theme";
import { HelmetProvider } from "react-helmet-async";
import { store } from "./store";
import { AuthProvider } from "./contexts/AuthContext";
import MarketingNav from "./components/MarketingNav";
import AppLayout from "./components/AppLayout";
import MarketingPage from "./pages/MarketingPage";
import Home from "./pages/Home";
import Players from "./pages/Players";
import PlayerDetails from "./pages/PlayerDetails";
import Compare from "./pages/Compare";
import QueryBuilder from "./pages/QueryBuilder";
import Leaderboards from "./pages/Leaderboards";
import NbaHome from "./pages/nba/NbaHome";
import NbaPlayers from "./pages/nba/NbaPlayers";
import NbaLeaderboards from "./pages/nba/NbaLeaderboards";
import NbaPlayerDetails from "./pages/nba/NbaPlayerDetails";
import NbaCompare from "./pages/nba/NbaCompare";
import NbaTeams from "./pages/nba/NbaTeams";
import NbaTeamDetails from "./pages/nba/NbaTeamDetails";
import NflHome from "./pages/nfl/NflHome";
import NflPlayers from "./pages/nfl/NflPlayers";
import NflLeaderboards from "./pages/nfl/NflLeaderboards";
import NflPlayerDetails from "./pages/nfl/NflPlayerDetails";
import NflCompare from "./pages/nfl/NflCompare";
import NflTeams from "./pages/nfl/NflTeams";
import NflTeamDetails from "./pages/nfl/NflTeamDetails";
import SportComingSoon from "./pages/SportComingSoon";
import Teams from "./pages/Teams";
import TeamDetails from "./pages/TeamDetails";
import GameDetailsPage from "./pages/GameDetailsPage";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import UserDashboard from "./pages/UserDashboard";
import Trivia from "./pages/Trivia";
import AdminDashboard from "./pages/AdminDashboard";
import AdminOps from "./pages/AdminOps";
import AdminTrivia from "./pages/AdminTrivia";
import AdminCommunity from "./pages/AdminCommunity";
import AdminSocial from "./pages/AdminSocial";
import AdminUsers from "./pages/AdminUsers";
import AdminRoute from "./components/AdminRoute";
import AuthCallback from "./pages/AuthCallback";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import About from "./pages/About";
import MyLineup from "./pages/MyLineup";
import SportLineup from "./pages/SportLineup";
import PublicFanPage from "./pages/PublicFanPage";
import TriviaShareVerify from "./pages/TriviaShareVerify";
import Community from "./pages/Community";
import CommunityBuild from "./pages/CommunityBuild";
import CommunityRoster from "./pages/CommunityRoster";
import CommunityMatchup from "./pages/CommunityMatchup";
import CommunityChallenge from "./pages/CommunityChallenge";
import CommunityChallengeCreate from "./pages/CommunityChallengeCreate";
import CommunityDebate from "./pages/CommunityDebate";
import CommunityDebateCreate from "./pages/CommunityDebateCreate";
import CommunityProfile from "./pages/CommunityProfile";
import PublicCommunityPage from "./pages/PublicCommunityPage";
import useGaPageViews from "./hooks/useGaPageViews";
import { getComingSoonPaths } from "./utils/sportWorkspace";

// Redirect components for dynamic routes
const PlayerRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/app/player/${id}`} replace />;
};

const TeamRedirect = () => {
  const { teamName } = useParams();
  return <Navigate to={`/app/teams/${teamName}`} replace />;
};

const comingSoonPaths = getComingSoonPaths();

const AppComingSoon = () => (
  <AppLayout>
    <SportComingSoon />
  </AppLayout>
);

function App() {
  // Track page views with Google Analytics
  useGaPageViews();

  return (
    <Provider store={store}>
      <AuthProvider>
        <HelmetProvider>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <Routes>
              {/* Marketing/Public Routes */}
              <Route
                path="/"
                element={
                  <>
                    <MarketingNav />
                    <MarketingPage />
                  </>
                }
              />

              {/* Auth Routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/auth/callback" element={<AuthCallback />} />

              {/* About Route */}
              <Route
                path="/about"
                element={
                  <>
                    <MarketingNav />
                    <About />
                  </>
                }
              />

              {/* Protected App Routes */}
              <Route
                path="/app"
                element={
                  <AppLayout>
                    <Home />
                  </AppLayout>
                }
              />
              <Route
                path="/app/players"
                element={
                  <AppLayout>
                    <Players />
                  </AppLayout>
                }
              />
              <Route
                path="/app/compare"
                element={
                  <AppLayout>
                    <Compare />
                  </AppLayout>
                }
              />
              <Route
                path="/app/queryBuilder"
                element={
                  <AppLayout>
                    <QueryBuilder />
                  </AppLayout>
                }
              />
              <Route
                path="/app/player/:id"
                element={
                  <AppLayout>
                    <PlayerDetails />
                  </AppLayout>
                }
              />
              <Route
                path="/app/all-time-greats"
                element={
                  <AppLayout>
                    <Leaderboards />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nba"
                element={
                  <AppLayout>
                    <NbaHome />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nba/players"
                element={
                  <AppLayout>
                    <NbaPlayers />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nba/all-time-greats"
                element={
                  <AppLayout>
                    <NbaLeaderboards />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nba/player/:id"
                element={
                  <AppLayout>
                    <NbaPlayerDetails />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nba/compare"
                element={
                  <AppLayout>
                    <NbaCompare />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nba/teams"
                element={
                  <AppLayout>
                    <NbaTeams />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nba/teams/:teamId"
                element={
                  <AppLayout>
                    <NbaTeamDetails />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nba/queryBuilder"
                element={
                  <AppLayout>
                    <QueryBuilder />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nfl"
                element={
                  <AppLayout>
                    <NflHome />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nfl/players"
                element={
                  <AppLayout>
                    <NflPlayers />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nfl/all-time-greats"
                element={
                  <AppLayout>
                    <NflLeaderboards />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nfl/player/:id"
                element={
                  <AppLayout>
                    <NflPlayerDetails />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nfl/compare"
                element={
                  <AppLayout>
                    <NflCompare />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nfl/teams"
                element={
                  <AppLayout>
                    <NflTeams />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nfl/teams/:teamId"
                element={
                  <AppLayout>
                    <NflTeamDetails />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nfl/queryBuilder"
                element={
                  <AppLayout>
                    <QueryBuilder />
                  </AppLayout>
                }
              />
              {comingSoonPaths.map((path) => (
                <Route key={path} path={path} element={<AppComingSoon />} />
              ))}
              <Route
                path="/app/trivia"
                element={
                  <AppLayout>
                    <Trivia />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nba/trivia"
                element={
                  <AppLayout>
                    <Trivia />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nfl/trivia"
                element={
                  <AppLayout>
                    <Trivia />
                  </AppLayout>
                }
              />
              <Route
                path="/app/community"
                element={
                  <AppLayout>
                    <Community />
                  </AppLayout>
                }
              />
              <Route
                path="/app/community/build"
                element={
                  <AppLayout>
                    <CommunityBuild />
                  </AppLayout>
                }
              />
              <Route
                path="/app/community/rosters/:idOrSlug"
                element={
                  <AppLayout>
                    <CommunityRoster />
                  </AppLayout>
                }
              />
              <Route
                path="/app/community/matchups/:id"
                element={
                  <AppLayout>
                    <CommunityMatchup />
                  </AppLayout>
                }
              />
              <Route
                path="/app/community/challenges/new"
                element={
                  <AppLayout>
                    <CommunityChallengeCreate />
                  </AppLayout>
                }
              />
              <Route
                path="/app/community/challenges/:idOrSlug"
                element={
                  <AppLayout>
                    <CommunityChallenge />
                  </AppLayout>
                }
              />
              <Route
                path="/app/community/debates/new"
                element={
                  <AppLayout>
                    <CommunityDebateCreate />
                  </AppLayout>
                }
              />
              <Route
                path="/app/community/debates/:idOrSlug"
                element={
                  <AppLayout>
                    <CommunityDebate />
                  </AppLayout>
                }
              />
              <Route
                path="/app/community/creators/:handleOrUsername"
                element={
                  <AppLayout>
                    <CommunityProfile />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nba/community"
                element={
                  <AppLayout>
                    <Community />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nba/community/build"
                element={
                  <AppLayout>
                    <CommunityBuild />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nba/community/rosters/:idOrSlug"
                element={
                  <AppLayout>
                    <CommunityRoster />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nba/community/matchups/:id"
                element={
                  <AppLayout>
                    <CommunityMatchup />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nba/community/challenges/new"
                element={
                  <AppLayout>
                    <CommunityChallengeCreate />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nba/community/challenges/:idOrSlug"
                element={
                  <AppLayout>
                    <CommunityChallenge />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nba/community/debates/new"
                element={
                  <AppLayout>
                    <CommunityDebateCreate />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nba/community/debates/:idOrSlug"
                element={
                  <AppLayout>
                    <CommunityDebate />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nba/community/creators/:handleOrUsername"
                element={
                  <AppLayout>
                    <CommunityProfile />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nfl/community"
                element={
                  <AppLayout>
                    <Community />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nfl/community/build"
                element={
                  <AppLayout>
                    <CommunityBuild />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nfl/community/rosters/:idOrSlug"
                element={
                  <AppLayout>
                    <CommunityRoster />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nfl/community/matchups/:id"
                element={
                  <AppLayout>
                    <CommunityMatchup />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nfl/community/challenges/new"
                element={
                  <AppLayout>
                    <CommunityChallengeCreate />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nfl/community/challenges/:idOrSlug"
                element={
                  <AppLayout>
                    <CommunityChallenge />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nfl/community/debates/new"
                element={
                  <AppLayout>
                    <CommunityDebateCreate />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nfl/community/debates/:idOrSlug"
                element={
                  <AppLayout>
                    <CommunityDebate />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nfl/community/creators/:handleOrUsername"
                element={
                  <AppLayout>
                    <CommunityProfile />
                  </AppLayout>
                }
              />
              <Route
                path="/app/teams"
                element={
                  <AppLayout>
                    <Teams />
                  </AppLayout>
                }
              />
              <Route
                path="/app/teams/:teamName"
                element={
                  <AppLayout>
                    <TeamDetails />
                  </AppLayout>
                }
              />
              <Route
                path="/app/game/:gamePk"
                element={
                  <AppLayout>
                    <GameDetailsPage />
                  </AppLayout>
                }
              />
              <Route
                path="/app/lineup"
                element={
                  <AppLayout>
                    <MyLineup />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nba/lineup"
                element={
                  <AppLayout>
                    <SportLineup />
                  </AppLayout>
                }
              />
              <Route
                path="/app/nfl/lineup"
                element={
                  <AppLayout>
                    <SportLineup />
                  </AppLayout>
                }
              />
              <Route
                path="/app/profile"
                element={
                  <AppLayout>
                    <Profile />
                  </AppLayout>
                }
              />
              <Route
                path="/app/settings"
                element={
                  <AppLayout>
                    <Settings />
                  </AppLayout>
                }
              />
              <Route
                path="/app/dashboard"
                element={
                  <AppLayout>
                    <UserDashboard />
                  </AppLayout>
                }
              />
              <Route
                path="/app/admin/dashboard"
                element={
                  <AppLayout>
                    <AdminRoute>
                      <AdminDashboard />
                    </AdminRoute>
                  </AppLayout>
                }
              />
              <Route
                path="/app/admin/ops"
                element={
                  <AppLayout>
                    <AdminRoute>
                      <AdminOps />
                    </AdminRoute>
                  </AppLayout>
                }
              />
              <Route
                path="/app/admin/trivia"
                element={
                  <AppLayout>
                    <AdminRoute>
                      <AdminTrivia />
                    </AdminRoute>
                  </AppLayout>
                }
              />
              <Route
                path="/app/admin/community"
                element={
                  <AppLayout>
                    <AdminRoute>
                      <AdminCommunity />
                    </AdminRoute>
                  </AppLayout>
                }
              />
              <Route
                path="/app/admin/social"
                element={
                  <AppLayout>
                    <AdminRoute>
                      <AdminSocial />
                    </AdminRoute>
                  </AppLayout>
                }
              />
              <Route
                path="/app/admin/users"
                element={
                  <AppLayout>
                    <AdminRoute>
                      <AdminUsers />
                    </AdminRoute>
                  </AppLayout>
                }
              />
              {/* Redirect old admin route to dashboard */}
              <Route
                path="/app/admin"
                element={<Navigate to="/app/admin/dashboard" replace />}
              />

              {/* Legacy route redirects */}
              <Route path="/player/:id" element={<PlayerRedirect />} />
              <Route
                path="/compare"
                element={<Navigate to="/app/compare" replace />}
              />
              <Route
                path="/queryBuilder"
                element={<Navigate to="/app/queryBuilder" replace />}
              />
              <Route
                path="/leaderboards"
                element={<Navigate to="/app/all-time-greats" replace />}
              />
              <Route
                path="/app/leaderboards"
                element={<Navigate to="/app/all-time-greats" replace />}
              />
              <Route
                path="/teams"
                element={<Navigate to="/app/teams" replace />}
              />
              <Route path="/teams/:teamName" element={<TeamRedirect />} />

              <Route path="/u/:handle" element={<PublicFanPage />} />
              <Route path="/v/:token" element={<TriviaShareVerify />} />
              <Route
                path="/roster/:idOrSlug"
                element={
                  <PublicCommunityPage>
                    <CommunityRoster publicView />
                  </PublicCommunityPage>
                }
              />
              <Route
                path="/matchup/:id"
                element={
                  <PublicCommunityPage>
                    <CommunityMatchup publicView />
                  </PublicCommunityPage>
                }
              />
              <Route
                path="/challenge/:idOrSlug"
                element={
                  <PublicCommunityPage>
                    <CommunityChallenge publicView />
                  </PublicCommunityPage>
                }
              />
              <Route
                path="/debate/:idOrSlug"
                element={
                  <PublicCommunityPage>
                    <CommunityDebate publicView />
                  </PublicCommunityPage>
                }
              />

              {/* Redirect root app routes to marketing page */}
              <Route path="*" element={<MarketingPage />} />
            </Routes>
          </ThemeProvider>
        </HelmetProvider>
      </AuthProvider>
    </Provider>
  );
}

export default App;
